import { useState, useEffect } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import GodModeMap from './GodModeMap';
import { useTenant } from '../../context/TenantContext'; 
import { getTenantLeads } from '../../services/crmService'; 

export default function TelemetryCenter() {
  const { tenantId, loading: tenantLoading } = useTenant(); 
  const [isLoading, setIsLoading] = useState(true);
  
  // Dynamic Data States
  const [mrrData, setMrrData] = useState([]);
  const [contractorData, setContractorData] = useState([]);
  const [metrics, setMetrics] = useState({
    globalMrr: 0,
    totalSetupFees: 0,
    pipelineValue: 0,
    avgPerTenant: 0,
    activeTenants: 0
  });

  // --- ⚡️ LIVE FIREBASE AGGREGATION (MULTI-TENANT) ⚡️ ---
  useEffect(() => {
    const fetchTelemetry = async () => {
      // Do not attempt fetch if tenant is still resolving
      if (tenantLoading || !tenantId) return;

      setIsLoading(true);
      try {
        const deals = await getTenantLeads(tenantId);

        let totalMRR = 0;
        let totalSetup = 0;
        let pipeValue = 0;
        let activeCount = 0;
        const repActivations = {};
        
        // Setup last 6 months for the chart
        const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const last6Months = [];
        const monthlyRevenueTracker = {};
        
        for(let i = 5; i >= 0; i--) {
          const d = new Date();
          d.setMonth(d.getMonth() - i);
          const mName = monthNames[d.getMonth()];
          last6Months.push(mName);
          monthlyRevenueTracker[mName] = 0; // Initialize at 0
        }

        // SWEEP THE PIPELINE
        deals.forEach(deal => {
          // ENTERPRISE FIX: Capture every penny
          const mrr = Number(deal.mrr_value || deal.mrr || 0);
          const setup = Number(deal.calculatedOneTime || deal.setupFee || deal.amountPaid || 0);
          const totalValue = mrr + setup;
          
          if (deal.stage === 'closedWon' || deal.status === 'Active Tenant') {
            totalMRR += mrr;
            totalSetup += setup;
            activeCount++;

            // 1. Leaderboard Tracking
            const rep = deal.rep_name || 'Unassigned (Web Lead)';
            repActivations[rep] = (repActivations[rep] || 0) + 1;

            // 2. Trajectory Tracking (Map to month)
            if (deal.createdAt?.toDate) {
              const dealMonth = monthNames[deal.createdAt.toDate().getMonth()];
              if (monthlyRevenueTracker[dealMonth] !== undefined) {
                // Add the total captured deal value to the chart trajectory
                monthlyRevenueTracker[dealMonth] += totalValue;
              }
            }
          } else {
            // If it's not Closed/Won, it's in the pipeline
            pipeValue += totalValue;
          }
        });

        // Format Trajectory Data for Recharts
        let cumulativeRevenue = 0;
        const finalChartData = last6Months.map(month => {
          cumulativeRevenue += monthlyRevenueTracker[month]; 
          return { month, revenue: cumulativeRevenue };
        });

        // Format Leaderboard Data for Recharts
        const finalLeaderboard = Object.keys(repActivations)
          .map(name => ({
            name: name.split(' ')[0] + ' ' + (name.split(' ')[1]?.[0] || ''), // e.g. "Michael T"
            activations: repActivations[name]
          }))
          .sort((a, b) => b.activations - a.activations) // Highest first
          .slice(0, 5); // Top 5

        // Commit to State
        setMrrData(finalChartData);
        setContractorData(finalLeaderboard);
        setMetrics({
          globalMrr: totalMRR,
          totalSetupFees: totalSetup,
          pipelineValue: pipeValue,
          activeTenants: activeCount,
          avgPerTenant: activeCount > 0 ? (totalMRR / activeCount) : 0
        });

      } catch (error) {
        console.error("Failed to load telemetry:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTelemetry();
  }, [tenantId, tenantLoading]);

  return (
    <div className="animate-in fade-in duration-500 pb-12">
      {/* Header */}
      <div className="flex justify-between items-end mb-6 border-b border-slate-300 pb-4">
        <div>
          <h1 className="text-2xl font-black text-slate-900 tracking-tight uppercase">Telemetry Center</h1>
          <p className="text-slate-500 text-sm mt-1 font-medium">Global network performance and revenue aggregation.</p>
        </div>
        <div className="flex gap-6">
          <div className="text-right">
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Global MRR Run Rate</p>
            <p className="text-2xl font-black text-emerald-700">
              {isLoading || tenantLoading ? '...' : `$${(metrics.globalMrr / 1000).toFixed(1)}K`}
            </p>
          </div>
          <div className="text-right border-l border-slate-300 pl-6">
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Implementation & Setup Fees</p>
            <p className="text-2xl font-black text-slate-800">
              {isLoading || tenantLoading ? '...' : `$${(metrics.totalSetupFees / 1000).toFixed(1)}K`}
            </p>
          </div>
          <div className="text-right border-l border-slate-300 pl-6">
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Active Tenants</p>
            <p className="text-2xl font-black text-cyan-600">
              {isLoading || tenantLoading ? '...' : metrics.activeTenants}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Main Chart: Trajectory */}
        <div className="lg:col-span-2 bg-slate-900 border border-slate-800 shadow-xl flex flex-col relative">
          <div className="p-4 border-b border-slate-800 flex justify-between items-center">
            <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
              <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span> Network Gross Value Trajectory (6 Mo)
            </h3>
            <button className="text-[9px] font-bold text-slate-500 hover:text-white uppercase tracking-widest transition-colors">
              Export CSV
            </button>
          </div>
          
          <div className="p-4 flex-1 min-h-[300px]">
            {isLoading || tenantLoading ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mb-3"></div>
                <p className="text-[10px] font-bold text-emerald-500 uppercase tracking-widest animate-pulse">Aggregating Global Revenue...</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={mrrData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" vertical={false} />
                  <XAxis dataKey="month" stroke="#64748B" fontSize={10} tickLine={false} axisLine={false} />
                  <YAxis stroke="#64748B" fontSize={10} tickLine={false} axisLine={false} tickFormatter={(value) => `$${value/1000}k`} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#0F172A', border: '1px solid #1E293B', borderRadius: '0px' }}
                    itemStyle={{ color: '#10B981', fontSize: '12px', fontWeight: 'bold' }}
                    labelStyle={{ color: '#64748B', fontSize: '10px', textTransform: 'uppercase', marginBottom: '4px' }}
                    formatter={(value) => [`$${value.toLocaleString()}`, 'Cumulative Revenue']}
                  />
                  <Area type="monotone" dataKey="revenue" stroke="#10B981" strokeWidth={3} fillOpacity={1} fill="url(#colorRevenue)" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Side Panel: Contractor Leaderboard */}
        <div className="bg-white border border-slate-300 shadow-sm flex flex-col relative">
          <div className="bg-slate-100 p-4 border-b border-slate-300">
            <h3 className="text-[10px] font-bold text-slate-600 uppercase tracking-widest">Kinetic Output Leaderboard</h3>
          </div>
          
          <div className="p-4 flex-1 min-h-[300px]">
            {isLoading || tenantLoading ? (
               <div className="absolute inset-0 flex items-center justify-center">
                 <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Scanning Fleet...</p>
               </div>
            ) : contractorData.length === 0 ? (
               <div className="absolute inset-0 flex items-center justify-center">
                 <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">No Active Deals</p>
               </div>
            ) : (
               <ResponsiveContainer width="100%" height="100%">
                <BarChart data={contractorData} layout="vertical" margin={{ top: 0, right: 0, left: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                  <XAxis type="number" hide />
                  <YAxis dataKey="name" type="category" stroke="#475569" fontSize={10} tickLine={false} axisLine={false} width={70} />
                  <Tooltip 
                    cursor={{fill: '#F8FAFC'}}
                    contentStyle={{ backgroundColor: '#ffffff', border: '1px solid #E2E8F0', borderRadius: '0px' }}
                    itemStyle={{ color: '#0F172A', fontSize: '12px', fontWeight: 'bold' }}
                    formatter={(value) => [`${value} Activations`, 'Volume']}
                  />
                  <Bar dataKey="activations" fill="#0F172A" radius={[0, 4, 4, 0]} barSize={20} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

      </div>

      {/* Financial Health Row */}
      <div className="grid grid-cols-3 gap-6 mt-6">
        <div className="bg-white border border-slate-300 shadow-sm p-5 border-l-4 border-l-emerald-500">
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Avg MRR Per Tenant</p>
          <p className="text-xl font-black text-slate-800">
            {isLoading || tenantLoading ? '...' : `$${metrics.avgPerTenant.toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})}`}
          </p>
        </div>
        <div className="bg-white border border-slate-300 shadow-sm p-5 border-l-4 border-l-blue-500">
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Total Pipeline Value</p>
          <p className="text-xl font-black text-slate-800">
            {isLoading || tenantLoading ? '...' : `$${metrics.pipelineValue.toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})}`}
          </p>
        </div>
        <div className="bg-white border border-slate-300 shadow-sm p-5 border-l-4 border-l-amber-500">
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Projected Churn Risk</p>
          <p className="text-xl font-black text-slate-800">1.2%</p> {/* Placeholder pending compliance/support data */}
        </div>
      </div>

      {/* God Mode Kinetic Map Engine */}
      <div className="mt-6">
        <GodModeMap />
      </div>

    </div>
  );
}